import { buildTimeline } from "../../helpers/format-timeline";
import { WarningSchema } from "@/schemas/warning-data-schema";
import { Bar, BarChart, XAxis, YAxis, YAxisTickContentProps } from "recharts";
import { ITimelineEvents, ITimelineFormatted } from "@/types/timeline-type";
import { useEffect, useMemo, useState } from "@wordpress/element";
import { CustomTimelineBarProps } from "@/types/recharts-type";
import { DEFAULT_TIMELINE } from "../../constant/timline-constant";

import TimelineBar from "../TimelineBar";
import CustomYAxisTick from "../CustomYAxisTick";

import "./style.scss";

export const severityColor = {
	minor: "green",
	moderate: "yellow",
	severe: "orange",
	extreme: "red",
};

type TimeLinesContentProps = {
	selectedPolygonId?: string;
	warningData: WarningSchema[];
};

export const TimeLinesContent = ({
	selectedPolygonId,
	warningData,
}: TimeLinesContentProps) => {
	const [tooltipData, setTooltipData] = useState<
		ITimelineFormatted | undefined
	>(undefined);

	const findWarningData = useMemo(() => {
		return warningData.filter((item) => item.geometry_id === selectedPolygonId);
	}, [warningData, selectedPolygonId]);

	const timelineFormatted = useMemo(() => {
		return findWarningData.length > 0
			? buildTimeline(findWarningData)
			: DEFAULT_TIMELINE;
	}, [findWarningData]);

	useEffect(() => {
		setTooltipData(undefined);
	}, [timelineFormatted]);

	return (
		<div className="timeline-content">
			<BarChart
				height={timelineFormatted.length * 40}
				width={340}
				layout="vertical"
				data={timelineFormatted}
				barCategoryGap={12}
			>
				<XAxis type="number" domain={[0, 86400]} hide />
				<YAxis
					width={40}
					type="category"
					dataKey="event"
					tick={(props: YAxisTickContentProps) => {
						const item = timelineFormatted.find(
							(d) => d.event === props.payload.value,
						);

						if (!item) return null;

						return (
							<CustomYAxisTick
								x={Number(props.x)}
								y={Number(props.y)}
								payload={item}
							/>
						);
					}}
					tickLine={false}
					axisLine={false}
				/>

				<Bar
					dataKey="value"
					shape={(props: CustomTimelineBarProps) => (
						<TimelineBar
							x={props.x!}
							y={props.y!}
							height={props.height!}
							width={props.width!}
							payload={props.payload as ITimelineEvents}
							onClick={({
								value,
							}: {
								value?: ITimelineFormatted;
								axis: number;
							}) => {
								setTooltipData(value);
							}}
						/>
					)}
					height={20}
				/>
			</BarChart>

			{tooltipData && (
				<div className="timeline-tooltip">
					<div className="tooltip-header">
						<img
							className="tooltip-icon"
							src={tooltipData.icon}
							alt={`${tooltipData.event} event icon`}
						/>

						<div className="tooltip-title">
							<span className="tooltip-type">
								{tooltipData.event.split("_")[0] ?? tooltipData.event}
							</span>
							<span className="tooltip-severity">{tooltipData.severity}</span>
						</div>
					</div>

					<div className="tooltip-content">
						<div className="tooltip-row">
							<span className="tooltip-label">Start</span>
							<span className="tooltip-value">
								{tooltipData.startDateToDisplay}
							</span>
						</div>

						<div className="tooltip-row">
							<span className="tooltip-label">End</span>
							<span className="tooltip-value">
								{tooltipData.endDateToDisplay}
							</span>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default TimeLinesContent;
